/**
 * WI-B-08 — cookie-mode session bootstrap (Option 1, live WorkOS leg).
 *
 * The live counterpart to the token/exchange machine in workos-callback.ts. In
 * Option 1 the coordinator owns the entire WorkOS handshake and holds the session
 * in an httpOnly SEALED COOKIE the browser never reads. There is therefore no
 * token in JS to verify here (workos-identity.ts) — the coordinator already
 * verified it when it minted the cookie. The client's whole job is:
 *
 *   1. beginLogin  -> navigate to GET /auth/login (the coordinator 302s to the
 *      hosted AuthKit page; the round-trip lands back on /auth/callback, which
 *      sets the cookie and 302s to "/"). This function never returns — the page
 *      is replaced.
 *   2. bootstrapCookieSession -> on app load, GET /auth/session with the cookie
 *      (credentials:"include"). 200 => an EstablishedSession built straight from
 *      the coordinator-verified scope; 401 => no session (show A1 launch); 403 =>
 *      verified-but-not-provisioned (a stranger to the roster).
 *
 * No bearer is ever held: every subsequent /mark/* call rides the same cookie
 * (the transport sends credentials:"include"). config.bearer is "" by design.
 */

import type { EstablishedSession } from "./workos-session";
import { deriveConfigRole } from "./workos-session";
import type { WorkosRole } from "./workos-mock";

/** The coordinator's login entry (GET). Server 302s to hosted AuthKit. */
export const AUTH_LOGIN_PATH = "/auth/login";
/** The coordinator's cookie-session probe (GET). Returns scope or 401/403. */
export const AUTH_SESSION_PATH = "/auth/session";
/** The coordinator's logout entry (POST). Clears the sealed httpOnly cookie. */
export const AUTH_LOGOUT_PATH = "/auth/logout";

/** What GET /auth/session returns on 200 — the coordinator Actor's scope. Role
 * is the coordinator Actor.role (member|supervisor|tenant_admin); we map it back
 * to the WorkOS authz role only to feed deriveConfigRole, keeping the two-axis
 * contract (workos-session.ts) intact. */
interface SessionResponse {
  tenant_id: string;
  project_ids: string[];
  role: string;
  display_name: string | null;
}

/** The bootstrap outcome the gate routes on, mirroring resolveCallback's kinds so
 * AuthedApp can branch identically: an established session, a not-provisioned
 * stranger (A6-ish), or "none" (show launch / an error screen). */
export type CookieBootstrap =
  | { kind: "session"; session: EstablishedSession }
  | { kind: "not-provisioned"; display: string }
  | { kind: "none" }
  | { kind: "error" };

/** Map the coordinator Actor.role back onto a WorkOS authz role so the existing
 * deriveConfigRole (the single UI-tier authority) stays the one place tiers are
 * decided. tenant_admin<->admin; member/supervisor pass through. */
function authzRoleFromActorRole(role: string): WorkosRole | null {
  switch (role) {
    case "tenant_admin":
      return "admin";
    case "supervisor":
      return "supervisor";
    case "member":
      return "member";
    default:
      return null;
  }
}

/** Redirect the browser to the coordinator login entry. Never returns (the
 * document is replaced). Same-origin, so no CORS and the cookie flows. */
export function beginCookieLogin(): void {
  // Replace the unauthenticated launch entry. On iOS, assign() leaves the
  // launch/AuthKit/callback chain behind the authenticated app; one Back gesture
  // then appears to "log out" by revisiting that pre-session history.
  window.location.replace(AUTH_LOGIN_PATH);
}

/**
 * Sign out for real (Option 1): POST /auth/logout so the coordinator CLEARS the
 * sealed httpOnly cookie the browser can't touch, THEN hard-reload to "/". The
 * reload re-probes /auth/session with no cookie -> 401 -> A1 launch, a clean
 * signed-out state. Without the server round-trip the cookie survives and the
 * next probe re-establishes the very session the user asked to leave (the
 * fail-silent bounce back to the same screen). credentials:"include" so the
 * cookie is presented for deletion; same-origin, no CORS. Never throws — even a
 * failed POST falls through to the reload so the button always DOES something.
 */
export async function logoutCookieSession(
  endpoint = "",
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  try {
    await fetchImpl(`${endpoint}${AUTH_LOGOUT_PATH}`, {
      method: "POST",
      credentials: "include",
      redirect: "manual", // the route 302s to "/"; we do our own reload below
    });
  } catch {
    /* network hiccup — still fall through to the reload so the user is not stuck */
  }
  if (typeof window !== "undefined") {
    window.location.assign("/");
  }
}

/**
 * Probe the cookie session on app load. Pure of DOM beyond fetch; the endpoint
 * base is injectable for tests. Never throws — a network failure becomes
 * { kind: "error" } so the gate always has a screen.
 */
export async function bootstrapCookieSession(
  endpoint = "",
  fetchImpl: typeof fetch = fetch,
): Promise<CookieBootstrap> {
  let res: Response;
  try {
    res = await fetchImpl(`${endpoint}${AUTH_SESSION_PATH}`, {
      method: "GET",
      credentials: "include",
    });
  } catch {
    return { kind: "error" };
  }
  if (res.status === 401) return { kind: "none" };
  if (res.status === 403) {
    let display = "your account";
    try {
      const b = (await res.json()) as { display_name?: string };
      if (b.display_name) display = b.display_name;
    } catch {
      /* keep default */
    }
    return { kind: "not-provisioned", display };
  }
  if (!res.ok) return { kind: "error" };

  let body: SessionResponse;
  try {
    body = (await res.json()) as SessionResponse;
  } catch {
    return { kind: "error" };
  }
  if (
    typeof body.tenant_id !== "string" ||
    !body.tenant_id ||
    !Array.isArray(body.project_ids)
  ) {
    return { kind: "error" };
  }

  let whoami: Response;
  try {
    whoami = await fetchImpl(`${endpoint}/mark/whoami`, { credentials: "include" });
  } catch {
    return { kind: "error" };
  }
  if (!whoami.ok) return { kind: "error" };
  let identity: {
    payload?: {
      tenant_id?: unknown;
      actor_id?: unknown;
      role?: unknown;
      project_ids?: unknown;
    };
  };
  try {
    identity = (await whoami.json()) as typeof identity;
  } catch {
    return { kind: "error" };
  }
  const actorId = identity.payload?.actor_id;
  const identityTenant = identity.payload?.tenant_id;
  const identityRole = identity.payload?.role;
  const identityProjects = identity.payload?.project_ids;
  if (
    typeof actorId !== "string"
    || !actorId.trim()
    || typeof identityTenant !== "string"
    || !identityTenant
    || typeof identityRole !== "string"
    || !Array.isArray(identityProjects)
    || !identityProjects.every((projectId) => typeof projectId === "string" && projectId.length > 0)
  ) return { kind: "error" };

  // Identity, scope, and role come from ONE authenticated response. The first
  // probe establishes that a cookie exists; it is not an authority after a
  // possible cross-tab cookie change between requests.
  const authzRole = authzRoleFromActorRole(identityRole);
  if (authzRole === null) return { kind: "error" };
  const session: EstablishedSession = {
    current_user: {
      // The coordinator resolved identity from the sealed cookie; the PWA holds
      // only the scope it returns. The stable actor id comes from the same
      // authenticated cookie via /mark/whoami and binds this device's queue.
      user_id: actorId,
      org_id: identityTenant,
      role: authzRole,
    },
    config: {
      tenant_id: identityTenant,
      project_ids: identityProjects as string[],
      role: deriveConfigRole(authzRole),
      bearer: "", // Option 1: no client bearer — the cookie is the credential.
    },
  };
  return { kind: "session", session };
}
